/**
 * React Query hooks for CV duplicate operations.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import cvService from '../services/cv.service';

interface UseCVDuplicatesQueryParams {
  cvId: string | undefined;
  workspaceId: string | undefined;
  idToken: string | undefined;
  enabled?: boolean;
}

export const useCVDuplicatesQuery = ({
  cvId,
  workspaceId,
  idToken,
  enabled = true,
}: UseCVDuplicatesQueryParams) => {
  return useQuery({
    queryKey: ['cv-duplicates', cvId],
    queryFn: async () => {
      if (!cvId || !workspaceId || !idToken) {
        throw new Error('CV ID, Workspace ID, and token are required');
      }
      return await cvService.getDuplicates(cvId, workspaceId, idToken);
    },
    enabled: enabled && !!cvId && !!workspaceId && !!idToken,
    staleTime: 30000,
    gcTime: 5 * 60 * 1000,
  });
};

interface UseMergeDuplicateMutationParams {
  cvId: string;
  workspaceId: string | undefined;
  idToken: string | undefined;
}

export const useMergeDuplicateMutation = ({
  cvId,
  workspaceId,
  idToken,
}: UseMergeDuplicateMutationParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (duplicateId: string) => {
      if (!workspaceId || !idToken) {
        throw new Error('Workspace ID and token are required');
      }
      return cvService.mergeDuplicate(cvId, duplicateId, workspaceId, idToken);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cv', cvId] });
      queryClient.invalidateQueries({ queryKey: ['cv-duplicates', cvId] });
      queryClient.invalidateQueries({ queryKey: ['cvs'] });
    },
  });
};

export const useCheckDuplicatesMutation = ({
  cvId,
  workspaceId,
  idToken,
}: UseMergeDuplicateMutationParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (!workspaceId || !idToken) {
        throw new Error('Workspace ID and token are required');
      }
      return cvService.checkDuplicates(cvId, workspaceId, idToken);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cv-duplicates', cvId] });
    },
  });
};

export const useDismissDuplicateMutation = ({
  cvId,
  workspaceId,
  idToken,
}: UseMergeDuplicateMutationParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (duplicateId: string) => {
      if (!workspaceId || !idToken) {
        throw new Error('Workspace ID and token are required');
      }
      return cvService.dismissDuplicate(cvId, duplicateId, workspaceId, idToken);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cv-duplicates', cvId] });
    },
  });
};
